import { useMemo } from "react"
import moment from "moment"
import { useSelector } from "react-redux"
import { formatEventLabels } from "pagesV2/pro/Calendar/utils"
import { UNASSIGNED_ID } from "../constants"

const buildCrewDayMap = (crew, calendarDays, events) => {
  const dayMap = {}
  calendarDays.forEach((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    dayMap[dayStr] = events
      .filter((ev) =>
        moment(ev.date).isSame(day, "day") &&
        (ev.assigned_crew || []).some((c) => c.profile_id === crew.id || c.id === crew.id || c.name === crew.name)
      )
      .map(formatEventLabels)
  })
  return dayMap
}

const useCrewGridData = (calendarDays, events, crewIds, roleIds) => {
  const calendarCrew = useSelector((state) => state.staff?.calendarCrew || [])

  const crewMembers = useMemo(() => {
    const active = calendarCrew
      .filter((c) => c.status?.toLowerCase() === "active")
      .map((c) => ({ id: c.id, name: c.name }))

    const assignedCrewIds = new Set()
    if (roleIds?.length > 0) {
      events.forEach((ev) => {
        (ev.assigned_crew || []).forEach((c) => {
          if (c.profile_id) assignedCrewIds.add(c.profile_id)
          if (c.id) assignedCrewIds.add(c.id)
        })
      })
    }

    if (crewIds?.length > 0 && roleIds?.length > 0) {
      const mergedIds = new Set([...crewIds.filter((id) => id !== UNASSIGNED_ID), ...assignedCrewIds])
      return active.filter((c) => mergedIds.has(c.id))
    }
    if (crewIds?.length > 0) return active.filter((c) => crewIds.includes(c.id))
    if (roleIds?.length > 0) return active.filter((c) => assignedCrewIds.has(c.id))
    return active
  }, [calendarCrew, crewIds, roleIds, events])

  const hasCrewFilters = crewIds?.filter((id) => id !== UNASSIGNED_ID)?.length > 0
  const hasRoleFilters = roleIds?.length > 0
  const hasAnyFilters = hasCrewFilters || hasRoleFilters
  const showUnassigned = crewIds?.includes(UNASSIGNED_ID) || !hasAnyFilters

  const eventsByCrewAndDay = useMemo(() => {
    const map = {}
    crewMembers.forEach((crew) => { map[crew.id] = buildCrewDayMap(crew, calendarDays, events) })
    return map
  }, [crewMembers, calendarDays, events])

  const unassignedEventsByDay = useMemo(() => {
    const dayMap = {}
    calendarDays.forEach((day) => {
      const dayStr = day.format("YYYY-MM-DD")
      dayMap[dayStr] = events
        .filter((ev) => moment(ev.date).isSame(day, "day") && (!ev.assigned_crew || ev.assigned_crew.length === 0))
        .map(formatEventLabels)
    })
    return dayMap
  }, [calendarDays, events])

  return { crewMembers, showUnassigned, eventsByCrewAndDay, unassignedEventsByDay }
}

export default useCrewGridData
